/* eslint-disable no-param-reassign */
import axios, {
    AxiosError,
    AxiosRequestConfig,
    AxiosRequestHeaders,
} from "axios";
import * as Sentry from "@sentry/react";
import { auth } from "../firebase";

const baseURL = import.meta.env.VITE_PATIENT_PORTAL_API_BASE_URL;
// TODO: Reduce this timeout after the current long-running API operations are optimized.
const REQUEST_TIMEOUT = 120000;

if (!baseURL) {
    throw new Error("VITE_PATIENT_PORTAL_API_BASE_URL is not defined");
}

const axiosInstance = axios.create({
    baseURL,
});

interface RetryAxiosRequestConfig extends AxiosRequestConfig {
    retry?: boolean;
}

type UnauthorizedLogoutHandler = () => Promise<void> | void;

let onUnauthorizedLogout: UnauthorizedLogoutHandler | null = null;
let isLoggingOut = false;

export function setUnauthorizedLogoutHandler(
    handler: UnauthorizedLogoutHandler | null
) {
    onUnauthorizedLogout = handler;
}

async function runUnauthorizedLogout() {
    if (!onUnauthorizedLogout || isLoggingOut) {
        return;
    }

    isLoggingOut = true;

    try {
        await onUnauthorizedLogout();
    } finally {
        isLoggingOut = false;
    }
}

function isBootstrapRequest(config: AxiosRequestConfig): boolean {
    const method = config.method?.toLowerCase();
    const url = config.url ?? "";

    if (method === "post" && /^\/?validate-user(?:\?.*)?$/.test(url)) {
        return true;
    }

    if (method === "get" && /^\/?user\/modern(?:\?.*)?$/.test(url)) {
        return true;
    }

    if (method === "get" && /^\/?user\/[^/]+\/practices(?:\?.*)?$/.test(url)) {
        return true;
    }

    if (method === "post" && /^\/?auth\/otp\/send(?:\?.*)?$/.test(url)) {
        return true;
    }

    if (method === "post" && /^\/?auth\/otp\/verify(?:\?.*)?$/.test(url)) {
        return true;
    }

    return false;
}

function isPublicRequest(config: AxiosRequestConfig): boolean {
    const method = config.method?.toLowerCase();
    const url = config.url ?? "";

    return method === "post" && /^\/?validate-user(?:\?.*)?$/.test(url);
}

function getPracticeId(): string | undefined {
    const practice = sessionStorage.getItem("practice");
    if (!practice) {
        return undefined;
    }

    const parsedPractice = JSON.parse(practice);
    return parsedPractice?.practice_id;
}

function getClientId(): string | undefined {
    return sessionStorage.getItem("clientId") ?? undefined;
}

async function getAccessToken(
    forceRefresh = false
): Promise<string | undefined> {
    if (!auth.currentUser) {
        return undefined;
    }

    return auth.currentUser.getIdToken(forceRefresh);
}

function sanitizeParams(params: unknown): unknown {
    if (
        !params ||
        typeof params !== "object" ||
        params instanceof URLSearchParams
    ) {
        return params;
    }

    return Object.fromEntries(
        Object.entries(params).map(([key, value]) => [
            key,
            typeof value === "string" ? value.replace(/,+/g, "") : value,
        ])
    );
}

function logAxiosWarning(
    message: string,
    config?: AxiosRequestConfig,
    extra?: Record<string, unknown>
) {
    Sentry.logger.warn(message, {
        area: "axios",
        method: config?.method,
        url: config?.url,
        ...extra,
    });
}

function captureAxiosFailure(
    message: string,
    config?: AxiosRequestConfig,
    extra?: Record<string, unknown>
) {
    Sentry.withScope((scope) => {
        scope.setLevel("warning");
        scope.setTag("area", "axios");
        scope.setContext("request", {
            method: config?.method ?? "unknown",
            url: config?.url ?? "unknown",
            ...extra,
        });
        Sentry.captureException(new Error(message));
    });
}

function buildRetryRequestConfig(
    originalRequest: RetryAxiosRequestConfig,
    token: string
): RetryAxiosRequestConfig {
    const headers: Record<string, unknown> = {
        ...(originalRequest.headers ?? {}),
        Authorization: `Bearer ${token}`,
    };

    return {
        ...originalRequest,
        headers: headers as AxiosRequestConfig["headers"],
        signal: undefined,
    };
}

function hasAuthorizationHeader(headers: Record<string, unknown>) {
    return Boolean(headers.Authorization ?? headers.authorization);
}

function isBearerAuthorizationHeader(value: unknown) {
    return typeof value === "string" && value.startsWith("Bearer ");
}

axiosInstance.interceptors.request.use(
    async (config) => {
        const isAuthBootstrapRequest = isBootstrapRequest(config);
        const isUnauthenticatedPublicRequest = isPublicRequest(config);

        if (config.method?.toLowerCase() === "get") {
            config.timeout = REQUEST_TIMEOUT;
        } else {
            config.timeout = 0;
        }

        if (!config.signal) {
            const controller = new AbortController();
            config.signal = controller.signal;
        }

        let token = await getAccessToken();
        const practiceId = getPracticeId();
        const clientId = getClientId();
        const hasAuthenticatedUser = Boolean(auth.currentUser);

        if (
            hasAuthenticatedUser &&
            !isUnauthenticatedPublicRequest &&
            !isAuthBootstrapRequest &&
            !practiceId
        ) {
            logAxiosWarning("Missing practice_id in request context", config, {
                hasAuthenticatedUser,
            });
            throw new Error("Practice details not found");
        }

        if (
            hasAuthenticatedUser &&
            !isUnauthenticatedPublicRequest &&
            !isAuthBootstrapRequest &&
            !clientId
        ) {
            logAxiosWarning("Missing client_id in request context", config, {
                hasAuthenticatedUser,
            });
            throw new Error("Client details not found");
        }

        const headers: Record<string, unknown> = {
            ...(config.headers ?? {}),
        };

        const hasCustomAuthorizationHeader =
            hasAuthorizationHeader(headers) &&
            !isBearerAuthorizationHeader(
                headers.Authorization ?? headers.authorization
            );

        if (hasAuthenticatedUser && !hasCustomAuthorizationHeader && !token) {
            token = await getAccessToken(true);

            if (!token) {
                logAxiosWarning("Missing access token before request", config, {
                    userId: auth.currentUser?.uid ?? "unknown",
                });
                throw new Error("Missing access token for authenticated user");
            }
        }

        if (practiceId) {
            headers.practice_id = practiceId;
        }

        if (clientId) {
            headers.client_id = clientId;
        }

        if (token && !hasCustomAuthorizationHeader) {
            headers.Authorization = `Bearer ${token}`;
        }

        config.headers = headers as AxiosRequestHeaders;

        if (config.params) {
            config.params = sanitizeParams(config.params);
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let isRefreshing = false;
let failedQueue: {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
}[] = [];

function processQueue(error: unknown, token: string | null = null) {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else if (token) {
            prom.resolve(token);
        }
    });
    failedQueue = [];
}

axiosInstance.interceptors.response.use(
    function resFunction(response) {
        return response;
    },
    async function errFunction(error) {
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        const axiosError = error as AxiosError;

        if (axiosError.code === "ECONNABORTED") {
            logAxiosWarning("Request timeout", axiosError.config, {
                code: axiosError.code,
            });
            return Promise.reject(
                new Error(
                    "Request took longer than expected. Please try again."
                )
            );
        }

        const originalRequest = axiosError.config as
            RetryAxiosRequestConfig | undefined;

        if (axiosError.response?.status !== 401 || !originalRequest) {
            return Promise.reject(error);
        }

        if (originalRequest.retry) {
            captureAxiosFailure("Terminal 401 after retry", originalRequest, {
                status: axiosError.response?.status,
            });
            await runUnauthorizedLogout();
            return Promise.reject(error);
        }

        originalRequest.retry = true;

        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then((token) => {
                    const retryRequest = buildRetryRequestConfig(
                        originalRequest,
                        token
                    );
                    return axiosInstance(retryRequest);
                })
                .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
            const newToken = await getAccessToken(true);
            if (!newToken) {
                logAxiosWarning(
                    "Token refresh returned empty token",
                    originalRequest,
                    {
                        status: axiosError.response?.status,
                    }
                );
                throw axiosError;
            }

            processQueue(null, newToken);

            const retryRequest = buildRetryRequestConfig(
                originalRequest,
                newToken
            );
            return await axiosInstance(retryRequest);
        } catch (refreshError) {
            logAxiosWarning("Token refresh failed", originalRequest, {
                status: axiosError.response?.status,
                error:
                    refreshError instanceof Error
                        ? refreshError.message
                        : "unknown",
            });
            processQueue(refreshError, null);
            await runUnauthorizedLogout();
            throw refreshError;
        } finally {
            isRefreshing = false;
        }
    }
);

export default axiosInstance;
